import { useMutation, useQueryClient, QueryClient } from 'react-query';
import uniq from 'lodash/uniq';
import { useAccount } from '@azure/msal-react';
import { useApiPost, useApiDelete, useApiPatch, ApiError } from 'src/api';
import { useBuildVars } from 'src/config';
import {
  CUSTOMER_GROUP_SEARCH_URL,
  CustomerGroup,
} from './customerGroupSearchQuery';
import {
  getGroupCustomersUrl,
  useGetAllCustomerGroupMembers,
  Customer,
} from './customerSearchQuery';
import { useCountryCode } from './hooks';

export type CustomerGroupSubmitResponse = {
  id: string;
};

function handleError(err: ApiError) {
  if (err.status === 400 && err.response?.responsePreamble?.errors) {
    throw new ApiError(err.message, err.status, {
      ...err.response,
      errors: err.response?.responsePreamble?.errors.reduce(
        (acc: any, { errorMessage, fieldName }: any) => ({
          ...acc,
          [fieldName]: errorMessage,
        }),
        {}
      ),
    });
  }
  throw err;
}

export const getReactQueryOptions = (
  queryClient: QueryClient,
  queries: any[]
): any => ({
  onError: () => {
    queries.forEach((query) => queryClient.invalidateQueries(query));
  },
  onSuccess: () => {
    queries.forEach((query) => queryClient.invalidateQueries(query));
  },
});

export type CustomerGroupCreateRequest = Pick<
  CustomerGroup,
  'description' | 'name'
>;

export function useCustomerGroupCreate() {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const countryCode = useCountryCode();
  const apiPost = useApiPost({
    host: buildVars.CG_API_BASE,
  });
  return useMutation(
    ({ description, name }: CustomerGroupCreateRequest) =>
      apiPost(
        '/',
        {
          requestCustomersPreamble: {},
          requestPreamble: {
            createdBy: userName,
            description,
            name,
          },
        },
        { countryCode }
      ).catch(handleError),
    getReactQueryOptions(queryClient, [CUSTOMER_GROUP_SEARCH_URL])
  );
}

export type CustomerGroupDuplicateRequest = CustomerGroupCreateRequest & {
  id: string;
};

export function useCustomerGroupDuplicate() {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const countryCode = useCountryCode();
  const apiPost = useApiPost({
    host: buildVars.CG_API_BASE,
  });
  const getAllCustomerGroupMembers = useGetAllCustomerGroupMembers();
  return useMutation(
    ({ description, id, name }: CustomerGroupDuplicateRequest) =>
      getAllCustomerGroupMembers(id)
        .then((customers) =>
          apiPost(
            '/',
            {
              requestCustomersPreamble: {
                ...getMembersPayload(customers),
              },
              requestPreamble: {
                createdBy: userName,
                description,
                name,
              },
            },
            { countryCode }
          )
        )
        .catch(handleError),
    getReactQueryOptions(queryClient, [CUSTOMER_GROUP_SEARCH_URL])
  );
}

export type CustomerGroupUpdateRequest = {
  description?: string;
  id: string;
  status?: boolean;
};

export function useCustomerGroupUpdate(isProductGroups?: boolean) {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const countryCode = useCountryCode();
  const apiPatch = useApiPatch(
    isProductGroups ? buildVars.VP_API_BASE : buildVars.CG_API_BASE
  );
  return useMutation(
    ({ description, id, status }: CustomerGroupUpdateRequest) =>
      apiPatch(
        isProductGroups
          ? '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/UpdateProductGroup'
          : `/${id}`,
        isProductGroups
          ? {
              description,
              modifiedBy: userName,
              productGroupId: Number(id),
              status,
            }
          : {
              requestPreamble: {
                description,
                modifiedBy: userName,
                status,
              },
            },
        { countryCode }
      ).catch(handleError),
    getReactQueryOptions(queryClient, [
      isProductGroups
        ? '/system.webapi.gds/api/v1/GdsCoreApiProductGroup/GetProductGroups'
        : CUSTOMER_GROUP_SEARCH_URL,
    ])
  );
}

const getMembersPayload = (customers: Customer[]) => ({
  ...(customers?.length && {
    customerNumbers: uniq(customers.map((c) => c.customerNumber)),
  }),
});

export type CustomerUpdateMembersRequest = {
  customers: Customer[];
  reason: string;
};

export function useCustomerGroupAddMembers(id: string) {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const countryCode = useCountryCode();
  const apiPost = useApiPost({
    host: buildVars.CG_API_BASE,
  });
  return useMutation(
    ({ customers, reason }: CustomerUpdateMembersRequest) =>
      apiPost(
        getGroupCustomersUrl(id),
        {
          requestCustomersAddPreamble: {
            ...getMembersPayload(customers),
            modifiedBy: userName,
            reason,
          },
        },
        { countryCode }
      ).catch(handleError),
    getReactQueryOptions(queryClient, [
      CUSTOMER_GROUP_SEARCH_URL,
      getGroupCustomersUrl(id),
    ])
  );
}

export function useCustomerGroupRemoveMembers(id: string) {
  const queryClient = useQueryClient();
  const userName = useAccount()?.username ?? '';
  const buildVars = useBuildVars();
  const countryCode = useCountryCode();
  const apiDelete = useApiDelete(buildVars.CG_API_BASE);
  return useMutation(
    ({ customers, reason }: CustomerUpdateMembersRequest) =>
      apiDelete(
        getGroupCustomersUrl(id),
        {
          requestCustomersDeletePreamble: {
            ...getMembersPayload(customers),
            modifiedBy: userName,
            reason,
          },
        },
        { countryCode }
      ).catch(handleError),
    getReactQueryOptions(queryClient, [
      CUSTOMER_GROUP_SEARCH_URL,
      getGroupCustomersUrl(id),
    ])
  );
}
